import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  generateQrCode,
  getPlayerSubscription,
  playerDiscountPackageAdd,
  playerPayment,
} from "../../services/academicService";
import Select from "react-select";
import {
  getPublicDiscountType,
  getPublicPaymentType,
  getPublicPlanType,
} from "../../services/publicService";
import Swal from "sweetalert2";
import { DatePicker } from "rsuite";
import { formatDate, formatToLocalISO } from "../../utils/helperFunction";

const PlayerPaymentModal = ({ show, onClose, userData, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [packageData, setPackageData] = useState([]);
  const [paymentMode, setPaymentMode] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
  const [discountTypes, setDiscountTypes] = useState([]);
  const [selectedDiscountType, setSelectedDiscountType] = useState(null);
  const [planTypes, setPlanTypes] = useState([]);
  const [selectedPlanType, setSelectedPlanType] = useState(null);
  const [formData, setFormData] = useState({
    amountDeposited: "",
    receiptNo: "",
    description: "",
    discountValue: "",
    nextDueDate: "",
    paymentDate: new Date(),
  });
  const [qrData, setQrData] = useState(null);
  const [qrModalShow, setQrModalShow] = useState(false);

  const hasDueAmount = Number(userData?.dueAmount) > 0;
  const payableAmount = Number(
    userData?.playerSubscription?.payableAmount || 0,
  );
  const expectedAmount =
    userData?.paymentStatus === "Partial"
      ? Number(userData?.dueAmount || 0)
      : Number(userData?.playerSubscription?.payableAmount || 0);

  const depositedAmount = Number(formData.amountDeposited || 0);

  const showDueDate = depositedAmount > 0 && depositedAmount < expectedAmount;

  const handleDateChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    // setErrors({ ...errors, [name]: "" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (loading) return;

    if (selectedDiscountType?.value === "PERCENTAGE") {
      const percentage = Number(formData.discountValue);
      if (percentage < 0 || percentage > 100) {
        toast.error("Percentage must be between 0 and 100");
        return;
      }
    }
    if (
      !formData.amountDeposited ||
      !formData.receiptNo ||
      !selectedPaymentMode ||
      !formData.paymentDate
    ) {
      toast.error("All fields are required");
      return;
    }
    const depositedAmount = Number(formData.amountDeposited);
    const dueAmount = Number(userData?.dueAmount || 0);
    const payableAmount = Number(
      userData?.playerSubscription?.payableAmount || 0,
    );

    // Amount must be greater than 0
    if (depositedAmount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    // If payment status is PARTIAL
    if (userData?.paymentStatus === "PARTIAL") {
      if (depositedAmount > dueAmount) {
        toast.error(`Amount cannot be greater than due amount (₹${dueAmount})`);
        return;
      }
    } else {
      // Full payment
      if (depositedAmount > payableAmount) {
        toast.error(
          `Amount cannot be greater than payable amount (₹${payableAmount})`,
        );
        return;
      }
    }

    const discountValue = Number(formData.discountValue || 0);

    // Validate discount fields only when discount is greater than 0
    if (discountValue > 0) {
      if (!selectedDiscountType) {
        toast.error("Please select Discount Type");
        return;
      }

      if (!selectedPlanType) {
        toast.error("Please select Plan Type");
        return;
      }
    }

    if (showDueDate) {
      if (!formData?.nextDueDate) {
        toast.error("Please select next due date");
        return;
      }
    }

    const payload = {
      playerId: userData?.id,
      amount: Number(formData.amountDeposited),
      subscriptionId: hasDueAmount
        ? userData?.subscription?.id
        : selectedPackage?.value,
      receiptNumber: formData.receiptNo,
      paymentType: selectedPaymentMode?.value,
      paymentDate: formatToLocalISO(formData.paymentDate),
      nextDueDate: formData.nextDueDate
        ? formatDate(formData.nextDueDate)
        : null,
      description: formData.description,
    };
    console.log("Payment Payload:", payload);

    try {
      setLoading(true);
      const res = await playerPayment(payload);
      if (res) {
        // toast.success("Payment submitted successfully");
        onPaymentSuccess();
        resetForm();
        Swal.fire("Payment!", "Payment submitted successfully", "success");

      }
    } catch (error) {
      console.error("Payment error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackage();
    fetchPaymentModes();
    fetchDiscountTypes();
    fetchPlanTypes();
  }, []);

  async function fetchPackage() {
    try {
      const res = await getPlayerSubscription();
      setPackageData(res);
    } catch (error) {
      console.error("Error fetching package types:", error);
    }
  }

  async function fetchPaymentModes() {
    try {
      const res = await getPublicPaymentType();
      setPaymentMode(res);
    } catch (error) {
      console.error("Error fetching package types:", error);
    }
  }

  async function fetchDiscountTypes() {
    try {
      const res = await getPublicDiscountType();
      setDiscountTypes(res);
    } catch (error) {
      console.error("Error fetching discount types:", error);
    }
  }

  async function fetchPlanTypes() {
    try {
      const res = await getPublicPlanType();
      setPlanTypes(res);
    } catch (error) {
      console.error("Error fetching plan types:", error);
    }
  }

  useEffect(() => {
    if (userData?.subscription && packageData.length) {
      const matchedPackage = packageData.find(
        (pkg) => pkg.id === userData.subscription.id,
      );

      if (matchedPackage) {
        setSelectedPackage({
          value: matchedPackage.id,
          label: `${matchedPackage.packageType?.name} (Amount : ${matchedPackage.ammount} INR)`,
        });
      }
    }
  }, [userData?.subscription?.id, packageData, show]);

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setSelectedPackage(null);
    setSelectedPaymentMode(null);
    setSelectedDiscountType(null);
    setSelectedPlanType(null);

    setFormData({
      amountDeposited: "",
      receiptNo: "",
      description: "",
      discountValue: "",
      nextDueDate: "",
      paymentDate: new Date(),
    });
    setLoading(false);
  };

  // const handleAddDiscount = async () => {
  //   if (selectedDiscountType?.value === "PERCENTAGE") {
  //     const percentage = Number(formData.discountValue);
  //     if (percentage < 0 || percentage > 100) {
  //       toast.error("Percentage must be between 0 and 100");
  //       return;
  //     }
  //   }
  //   if (
  //     !formData.amountDeposited ||
  //     !formData.receiptNo ||
  //     !selectedPaymentMode
  //   ) {
  //     toast.error("All fields are required");
  //     return;
  //   }

  //   if (!hasDueAmount && !selectedPackage) {
  //     toast.error("Please select a package");
  //     return;
  //   }

  //   const discountValue = Number(formData.discountValue || 0);

  //   // Validate discount fields only when discount is greater than 0
  //   if (discountValue > 0) {
  //     if (!selectedDiscountType) {
  //       toast.error("Please select Discount Type");
  //       return;
  //     }

  //     if (!selectedPlanType) {
  //       toast.error("Please select Plan Type");
  //       return;
  //     }
  //   }
  //   try {
  //     const payload = {
  //       playerId: userData?.id,
  //       amount: Number(formData.amountDeposited),
  //       subscriptionId: hasDueAmount
  //         ? userData?.subscription?.id
  //         : selectedPackage?.value,
  //       receiptNumber: formData.receiptNo,
  //       paymentType: selectedPaymentMode?.value,
  //       nextDueDate: formData.nextDueDate,
  //       description: formData.description,
  //       // discountValue: Number(formData.discountValue),
  //       // discountValueType: selectedDiscountType?.value,
  //       // discountType: selectedPlanType?.value,
  //     };
  //     const res = await playerDiscountPackageAdd(payload);
  //     if (res) {
  //       Swal.fire(
  //         "Discount applied Successful!",
  //         "Player discount and package has been applied successfully.",
  //         "success",
  //       );
  //       handleClose();
  //     }
  //   } catch (error) {
  //     console.error("Error applying discount:", error);
  //     toast.error("Failed to apply discount. Please try again.");
  //   }
  // };

  const handleGenerateQR = async () => {
    const payload = {
      playerId: userData?.id,
      amount: Number(formData.amountDeposited),
    };
    const res = await generateQrCode(payload);
    if (res) {
      setQrData(res);
      setQrModalShow(true);
    }
  };

  useEffect(() => {
    if (!show) return;

    const amount = ["Partial", "Paid"].includes(userData?.paymentStatus)
      ? Number(userData?.dueAmount || 0)
      : Number(userData?.playerSubscription?.payableAmount || 0);

    setFormData((prev) => ({
      ...prev,
      amountDeposited: amount,
      paymentDate: prev.paymentDate || new Date(),
    }));
  }, [show, userData]);

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Payment</Modal.Title>
        </Modal.Header>

        <Modal.Body size="l">
          <div className="row">
            {/* Due Amount (NOW ALWAYS VISIBLE) */}
            <div className="col-sm-6 mb-3">
              <label className="form-label text-danger">Due Amount</label>
              <input
                type="number"
                name="dueAmount"
                placeholder="Due Amount"
                className="form-control"
                value={userData?.dueAmount ?? 0}
                readOnly
              />
            </div>

            <div className="col-sm-6">
              <div className="form-group">
                <label className="form-label">
                  Package <span className="text-danger">*</span>
                </label>
                <Select
                  isSearchable={false}
                  options={packageData?.map((pkg) => ({
                    value: pkg?.id,
                    label: `${pkg?.packageType?.name} (Amount : ${pkg?.ammount} INR)`,
                  }))}
                  placeholder="Select Package"
                  className="custom-react-select"
                  value={selectedPackage}
                  onChange={setSelectedPackage}
                  isDisabled
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "#e9ecef !important",
                    }),
                  }}
                />
              </div>
            </div>

            {userData?.playerSubscription?.isDiscount && (
              <>
                {userData?.playerSubscription?.discountValueType ===
                  "PERCENTAGE" ? (
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Discount (%)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`${userData?.playerSubscription?.discountValue}%`}
                      readOnly
                    />
                  </div>
                ) : (
                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Discount (₹)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`₹${userData?.playerSubscription?.discountValue}`}
                      readOnly
                    />
                  </div>
                )}
                <div className="col-sm-6 mb-3">
                  <label className="form-label">Amount to be Deposited</label>
                  <input
                    type="number"
                    name="dueAmount"
                    placeholder="Total Amount"
                    className="form-control"
                    value={userData?.playerSubscription?.payableAmount ?? 0}
                    readOnly
                  />
                </div>
              </>
            )}

            <div className="col-sm-6">
              <div className="form-group">
                <label className="form-label">
                  Payment Mode <span className="text-danger">*</span>
                </label>
                <Select
                  isSearchable={false}
                  options={paymentMode?.map((mode) => ({
                    value: mode?.value,
                    label: mode?.label,
                  }))}
                  placeholder="Select Payment Mode"
                  className="custom-react-select"
                  value={selectedPaymentMode}
                  onChange={setSelectedPaymentMode}
                />
              </div>
            </div>

            {/* Amount Deposited */}
            <div className="col-sm-6 mb-3">
              <label className="form-label">
                Amount Deposited <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                name="amountDeposited"
                placeholder="Enter ammount to be deposited"
                className="form-control"
                value={formData.amountDeposited}
                onChange={handleChange}
              />
            </div>

            {/* Receipt No */}
            <div className="col-sm-6 mb-3">
              <label className="form-label">
                Receipt No <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="receiptNo"
                className="form-control"
                placeholder="Enter receipt number"
                value={formData.receiptNo}
                onChange={handleChange}
              />
            </div>

            {/* Payment Date */}
            <div className="col-sm-6 mb-3">
              <div className="form-group">
                <label className="form-label">
                  Payment Date <span className="text-danger">*</span>
                </label>
                <div className="input-hasicon mb-xl-0 mb-3 z-3">
                  <DatePicker
                    placeholder="Select payment date"
                    className="picker-suit"
                    placement="auto"
                    value={formData.paymentDate}
                    onChange={(val) => handleDateChange("paymentDate", val)}
                    format="yyyy-MM-dd HH:mm:ss"
                    cleanable={false}
                  />
                  <div className="icon">
                    <i className="far fa-calendar" />
                  </div>
                </div>
              </div>
            </div>

            {showDueDate && (
              <div className="col-sm-6">
                <div className="form-group">
                  <label className="form-label" htmlFor="dob">
                    Next Due Date <span className="text-danger">*</span>
                  </label>
                  <div className="input-hasicon mb-xl-0 mb-3 z-3">
                    <DatePicker
                      placeholder="Select next due date"
                      className="picker-suit"
                      placement="auto"
                      value={formData.nextDueDate}
                      onChange={(val) => handleDateChange("nextDueDate", val)}
                      shouldDisableDate={(date) => date < new Date()}
                    />
                    <div className="icon">
                      <i className="far fa-calendar" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="col-sm-6 mb-3">
              <label className="form-label">Description</label>
              <input
                type="text"
                name="description"
                className="form-control"
                placeholder="Enter description"
                value={formData.description}
                onChange={handleChange}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {/* <Button variant="outline-info" onClick={handleGenerateQR}>
            Generate QR
          </Button> */}
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? "Processing..." : "Pay Now"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={qrModalShow}
        onHide={() => setQrModalShow(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Scan & Pay</Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "400px", overflow: "hidden" }}
        >
          <h4 className="fw-bold">Amount: ₹{qrData?.amount}</h4>
          <img
            src={qrData?.qrImageUrl}
            alt="QR Code"
            style={{
              width: "350px",
              height: "350px",
              objectFit: "cover",
              objectPosition: "left center", // adjust as needed
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setQrModalShow(false)}
          >
            Cancel
          </Button>
          <Button variant="outline-warning" onClick={handleGenerateQR}>
            Re-Generate
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const link = document.createElement("a");
              link.href = qrData?.qrImageUrl;
              link.download = "qr-code.png";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download QR
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PlayerPaymentModal;
